import { useNavigate } from "react-router-dom";
import { useRef, useState } from "react";
import { Sparkles, FolderArchive, MessagesSquare, ShoppingBasket, CalendarDays, Crown, Check, ArrowRight, Play, Pause, Volume2, VolumeX, Shield, Zap, Heart, Star } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import GooglePlayBadge from "@/components/GooglePlayBadge";

export default function LandingPage() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(true);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };
  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  const features = [
    { icon: FolderArchive, title: "Vault documenti intelligente", text: "Carica PDF, foto, ricevute. L'AI classifica, riconosce date di scadenza, persone e importi automaticamente." },
    { icon: Sparkles, title: "AI che conosce la tua famiglia", text: "Chiedi 'Quando scade l'assicurazione auto?' o 'Trova la garanzia della lavatrice'. Risposte in 2 secondi." },
    { icon: MessagesSquare, title: "Chat famiglia con AI integrata", text: "WhatsApp-style ma con un'AI che ascolta e crea automaticamente eventi e liste spesa dalle vostre conversazioni." },
    { icon: ShoppingBasket, title: "Lista spesa AI", text: "Scrivi 'dobbiamo comprare il latte' in chat e finisce automaticamente nella lista spesa condivisa." },
    { icon: CalendarDays, title: "Calendario famiglia", text: "Scadenze, appuntamenti medici, scuola, compleanni. Tutto in un unico posto, sincronizzato per tutti." },
    { icon: Heart, title: "Memoria emozionale", text: "I ricordi di una famiglia non sono solo file. Sono storie. Family Brain le custodisce." },
  ];

  return (
    <div className="min-h-screen bg-app text-primary-fg">
      {/* Nav */}
      <header className="sticky top-0 z-50 bg-glass border-b border-subtle px-5 md:px-10 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BrandLogo size={36} />
            <div className="font-display text-lg font-semibold">Family Brain</div>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="landing-login-link" onClick={() => navigate("/auth")} className="text-sm text-secondary-fg hover:text-primary-fg">Accedi</button>
            <button data-testid="landing-cta-nav" onClick={() => navigate("/auth")} className="btn-primary !py-2 !px-5 text-sm">Inizia gratis</button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="px-5 md:px-10 py-16 md:py-24">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-elevated border border-subtle text-xs text-secondary-fg mb-6">
              <Star className="w-3 h-3 text-amber-warm fill-amber-warm" />
              7 giorni Premium gratis · Nessuna carta richiesta
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
              La tua famiglia parla.<br />
              <span className="text-brand">Family Brain</span> organizza.
            </h1>
            <p className="text-secondary-fg text-base md:text-lg mt-6 leading-relaxed max-w-lg">
              L&apos;AI che organizza la vita di famiglia. Automaticamente.
            </p>

            <ul className="mt-6 space-y-2 text-sm md:text-base max-w-lg" data-testid="landing-benefits">
              {[
                "Ricorda le scadenze",
                "Organizza gli appuntamenti",
                "Gestisce la lista della spesa",
                "Archivia i documenti automaticamente",
              ].map((b) => (
                <li key={b} className="flex items-start gap-2 text-secondary-fg">
                  <Check className="w-4 h-4 mt-0.5 text-sage shrink-0" strokeWidth={2.4} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <button data-testid="landing-hero-cta" onClick={() => navigate("/auth")} className="btn-primary flex items-center justify-center gap-2 text-base !py-3">
                Crea la tua famiglia gratis <ArrowRight className="w-4 h-4" />
              </button>
              <button data-testid="landing-pricing-cta" onClick={() => navigate("/pricing-public")} className="btn-secondary flex items-center justify-center gap-2 text-base !py-3">
                Vedi i piani
              </button>
            </div>
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <span className="text-xs text-tertiary-fg">Oppure scarica l&apos;app:</span>
              <GooglePlayBadge testId="landing-hero-playstore" />
            </div>
            <div className="flex items-center gap-4 mt-8 text-xs text-tertiary-fg">
              <div className="flex items-center gap-1"><Shield className="w-3.5 h-3.5" /> Pagamenti sicuri Stripe</div>
              <div className="flex items-center gap-1"><Zap className="w-3.5 h-3.5" /> AI GPT-5.2</div>
              <div className="flex items-center gap-1"><Check className="w-3.5 h-3.5" /> GDPR-compliant</div>
            </div>
          </div>

          <div className="relative animate-fade-up" style={{ animationDelay: "150ms" }}>
            <div className="relative rounded-3xl overflow-hidden border border-subtle bg-surface aspect-video group">
              <video
                ref={videoRef}
                data-testid="landing-demo-video"
                poster="https://images.unsplash.com/photo-1511895426328-dc8714191300?w=900&q=80"
                className="w-full h-full object-cover"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                onPause={() => setPlaying(false)}
                onPlay={() => setPlaying(true)}
              >
                {/* WebM/VP9 first for patents-free decoders, MP4/H.264 fallback for Safari etc. */}
                <source src="/videos/familybrain_demo.webm" type="video/webm" />
                <source src="/videos/familybrain_demo.mp4" type="video/mp4" />
              </video>              {/* Subtle vignette so the controls stay readable over any frame */}
              <div className="absolute inset-0 bg-gradient-to-t from-app/60 via-transparent to-transparent pointer-events-none" />

              {/* Floating controls (no caption — the video speaks for itself) */}
              <div className="absolute bottom-4 right-4 flex items-center gap-2 pointer-events-auto">
                <button
                  type="button"
                  onClick={togglePlay}
                  data-testid="landing-video-playpause"
                  aria-label={playing ? "Metti in pausa" : "Riproduci"}
                  className="w-10 h-10 rounded-full bg-app/70 backdrop-blur-sm border border-white/10 grid place-items-center text-white hover:bg-app/90 transition"
                >
                  {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 fill-white" />}
                </button>
                <button
                  type="button"
                  onClick={toggleMute}
                  data-testid="landing-video-mute"
                  aria-label={muted ? "Attiva audio" : "Disattiva audio"}
                  className="w-10 h-10 rounded-full bg-app/70 backdrop-blur-sm border border-white/10 grid place-items-center text-white hover:bg-app/90 transition"
                >
                  {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="hidden md:flex items-start gap-3 mt-8 bg-surface border border-subtle rounded-2xl p-4 max-w-[420px] shadow-xl">
              <Sparkles className="w-4 h-4 text-brand shrink-0 mt-0.5" />
              <div>
                <div className="text-xs font-medium mb-1">AI risponde</div>
                <p className="text-xs text-secondary-fg leading-relaxed">&ldquo;L&apos;assicurazione auto scade il 15 marzo. Vuoi che ti ricordi tra una settimana?&rdquo;</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social proof / problema */}
      <section className="px-5 md:px-10 py-12 bg-surface border-y border-subtle">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm uppercase tracking-widest text-brand font-semibold mb-3">Il problema</p>
          <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tight leading-tight">
            &ldquo;Dov&apos;è la garanzia del frigorifero? Quando ho fatto la visita dal pediatra? Quando scade l&apos;auto?&rdquo;
          </h2>
          <p className="text-secondary-fg mt-4 max-w-2xl mx-auto">
            Ogni famiglia ha centinaia di documenti, scadenze e ricordi sparsi tra email, cassetti, gallerie del telefono. Family Brain mette ordine e ti dà una mente che ricorda tutto al posto tuo.
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="px-5 md:px-10 py-16 md:py-24">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-widest text-brand font-semibold">Cosa puoi fare</p>
            <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-3">Una piattaforma. <span className="text-brand">Tutta la famiglia.</span></h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((f, i) => (
              <div key={f.title} className="bg-surface border border-subtle rounded-2xl p-6 animate-fade-up" style={{ animationDelay: `${i * 60}ms` }}>
                <div className="w-12 h-12 rounded-2xl bg-elevated grid place-items-center mb-4">
                  <f.icon className="w-6 h-6 text-brand" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-lg font-medium">{f.title}</h3>
                <p className="text-sm text-secondary-fg mt-2 leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing teaser */}
      <section className="px-5 md:px-10 py-16 bg-surface border-y border-subtle">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs uppercase tracking-widest text-brand font-semibold">Prezzi semplici</p>
          <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-3">Inizia gratis. Cresci con noi.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-10">
            {[
              { name: "Free", price: "€0", tag: "Per iniziare", color: "var(--text-tertiary)" },
              { name: "Family", price: "€4.99", tag: "Più popolare", recommended: true, color: "#E07A5F" },
              { name: "Family AI+", price: "€9.99", tag: "AI avanzata", color: "#F2CC8F" },
            ].map((p) => (
              <div key={p.name} className={`rounded-2xl p-5 border ${p.recommended ? "bg-elevated border-brand" : "bg-app border-subtle"}`}>
                <div className="font-display text-lg font-medium">{p.name}</div>
                <div className="font-display text-3xl font-semibold mt-2">{p.price}<span className="text-sm text-tertiary-fg">/mese</span></div>
                <div className="text-xs text-tertiary-fg mt-1">{p.tag}</div>
              </div>
            ))}
          </div>
          <button data-testid="landing-see-pricing" onClick={() => navigate("/pricing-public")} className="btn-primary mt-8 inline-flex items-center gap-2">
            Confronta i piani <ArrowRight className="w-4 h-4" />
          </button>
          <p className="text-xs text-tertiary-fg mt-4">7 giorni Premium gratis · Annulli quando vuoi</p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-5 md:px-10 py-20">
        <div className="max-w-3xl mx-auto text-center">
          <Crown className="w-12 h-12 text-amber-warm mx-auto mb-4" strokeWidth={1.4} />
          <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">Pronti a costruire la vostra memoria?</h2>
          <p className="text-secondary-fg mt-4 text-base md:text-lg">7 giorni di Premium gratis. Nessuna carta. Annulli quando vuoi.</p>
          <button data-testid="landing-bottom-cta" onClick={() => navigate("/auth")} className="btn-primary mt-8 inline-flex items-center gap-2 text-base !py-3 !px-8">
            Inizia ora <ArrowRight className="w-4 h-4" />
          </button>
          <div className="mt-6 flex justify-center">
            <GooglePlayBadge testId="landing-bottom-playstore" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-subtle px-5 md:px-10 py-10">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between gap-6 text-sm text-tertiary-fg">
          <div className="flex items-center gap-3">
            <BrandLogo size={32} />
            <div>
              <div className="text-primary-fg font-medium">Family Brain</div>
              <div className="text-xs">© 2026 · La memoria della tua famiglia.</div>
            </div>
          </div>
          <div className="flex flex-col md:items-end gap-3">
            <GooglePlayBadge testId="landing-footer-playstore" width={160} className="!text-sm" />
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              <a href="/pricing-public" className="hover:text-primary-fg">Prezzi</a>
              <a href="/terms" data-testid="footer-terms-link" className="hover:text-primary-fg">Termini di Servizio</a>
              <a href="/privacy" data-testid="footer-privacy-link" className="hover:text-primary-fg">Privacy</a>
              <a href="/delete-account" data-testid="footer-delete-account-link" className="hover:text-primary-fg">Elimina account</a>
              <a href="mailto:info@familybrain.it" className="hover:text-primary-fg">Contatti</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
